import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { Api } from '../../api/api';
import { getScheduleTranslatorDebugger } from '../../api/functions';
import { ApiProblem } from '../../api/models/api-problem';
import { ScheduleTranslatorDebuggerAction } from '../../api/models/schedule-translator-debugger-action';
import { ScheduleTranslatorDebuggerSchedule } from '../../api/models/schedule-translator-debugger-schedule';
import { ScheduleTranslatorDebuggerResponse } from '../../api/models/schedule-translator-debugger-response';
import { ScheduleTranslatorDebuggerSolution } from '../../api/models/schedule-translator-debugger-solution';
import { LocationStateService } from '../../common/services/location-state.service';
import { ScheduleTranslatorDebuggerConfigValue } from '../../api/models/schedule-translator-debugger-config-value';
import { SolutionChartComponent } from './solution-chart.component';

/** Zeitzone, in der Lösungstage und Wechselrichter-Crons gelten. */
const LOCATION_TIME_ZONE = 'Europe/Vienna';

/** EUR/Wh → ct/kWh */
const EUR_PER_WH_TO_CT_PER_KWH = 100_000;

const TIME_FORMAT = new Intl.DateTimeFormat('de-AT', {
  timeZone: LOCATION_TIME_ZONE,
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

const DATE_TIME_FORMAT = new Intl.DateTimeFormat('de-AT', {
  timeZone: LOCATION_TIME_ZONE,
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

/** Kompakter Zeitstempel (Europe/Vienna) für die Tabellen: Tag, Monat und Uhrzeit. */
const TIMESTAMP_FORMAT = new Intl.DateTimeFormat('de-AT', {
  timeZone: LOCATION_TIME_ZONE,
  day: '2-digit',
  month: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

/** Spaltenüberschrift (deutsch) und Originalfeldname aus der API. */
interface Column {
  label: string;
  field: string;
}

const TIMESTEP_COLUMNS: Column[] = [
  { label: 'Zeit', field: 'timestamp' },
  { label: 'Bezug', field: 'consumption' },
  { label: 'Einsp.', field: 'feedin' },
  { label: 'Abregel.', field: 'curtail' },
  { label: 'Laden', field: 'charge' },
  { label: 'Entladen', field: 'discharge' },
  { label: 'SoC', field: 'soc' },
  { label: 'Ertrag Wh', field: 'yieldWh' },
  { label: 'Last Wh', field: 'loadWh' },
  { label: 'Bezug ct/kWh', field: 'priceConsumptionEurWh' },
  { label: 'Einsp. ct/kWh', field: 'priceFeedinEurWh' },
];

const ACTION_COLUMNS: Column[] = [
  { label: 'Zeit', field: 'cron' },
  { label: 'Aktion', field: 'type' },
  { label: 'Wert', field: 'value' },
  { label: 'Min SoC', field: 'minSoC' },
  { label: 'Max Kauf-SoC', field: 'maxPurchaseSoC' },
  { label: 'Register', field: 'registerName [registerIndex]' },
  { label: 'Cron', field: 'cron' },
];

/** Felder, die im Fahrplan-Vergleich gegenübergestellt werden (Register-Felder bewusst ausgenommen). */
const DIFF_FIELDS: { label: string; field: 'type' | 'value' | 'minSoC' | 'maxPurchaseSoC' }[] = [
  { label: 'Aktion', field: 'type' },
  { label: 'Wert', field: 'value' },
  { label: 'Min SoC', field: 'minSoC' },
  { label: 'Max Kauf-SoC', field: 'maxPurchaseSoC' },
];

const CONFIG_GROUP_LABELS: Record<string, string> = {
  schedule: 'Fahrplan (schedule)',
  battery: 'Batterie',
  grid: 'Netz',
  derived: 'Abgeleitet',
  global: 'Global',
};

const CONFIG_SOURCE_LABELS: Partial<Record<string, string>> = {
  LOCATION: 'Standort',
  DEFAULT: 'Standardwert',
  SOLUTION: 'aus Lösung',
  DERIVED: 'berechnet',
  UNSET: 'nicht gesetzt',
};

type DiffStatus = 'same' | 'changed' | 'manualOnly' | 'autoOnly';

interface ScheduleWithSolution {
  schedule: ScheduleTranslatorDebuggerSchedule;
  solution: ScheduleTranslatorDebuggerSolution;
}

interface DiffRow {
  time: string;
  manual?: ScheduleTranslatorDebuggerAction;
  auto?: ScheduleTranslatorDebuggerAction;
  /** Feldnamen aus {@link DIFF_FIELDS}, die sich unterscheiden. */
  changed: string[];
  status: DiffStatus;
}

interface ScheduleDiff {
  manual?: ScheduleWithSolution;
  auto?: ScheduleWithSolution;
  rows: DiffRow[];
}

const OFFSET_PARTS_FORMAT = new Intl.DateTimeFormat('en-US', {
  timeZone: LOCATION_TIME_ZONE,
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
  second: 'numeric',
  hourCycle: 'h23',
});

const DAY_FORMAT = new Intl.DateTimeFormat('en-CA', {
  timeZone: LOCATION_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/** Offset von Europe/Vienna gegenüber UTC in Millisekunden zum gegebenen Zeitpunkt. */
function viennaOffsetMs(instant: Date): number {
  const parts = Object.fromEntries(
    OFFSET_PARTS_FORMAT.formatToParts(instant).map((part) => [part.type, Number(part.value)]),
  ) as Record<string, number>;
  const asUtc = Date.UTC(parts['year'], parts['month'] - 1, parts['day'], parts['hour'], parts['minute'], parts['second']);
  return asUtc - Math.floor(instant.getTime() / 1000) * 1000;
}

/** Wandelt eine Wanduhrzeit in Europe/Vienna in einen absoluten Zeitpunkt um. */
function viennaWallTimeToDate(year: number, month: number, day: number, hour: number, minute: number, second: number): Date {
  const wallAsUtc = Date.UTC(year, month - 1, day, hour, minute, second);
  const firstGuess = wallAsUtc - viennaOffsetMs(new Date(wallAsUtc));
  // Zweiter Schritt korrigiert, falls der Offset an einer Sommer-/Winterzeitgrenze wechselt.
  return new Date(wallAsUtc - viennaOffsetMs(new Date(firstGuess)));
}

@Component({
  selector: 'app-schedule-translator-debugger',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatExpansionModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatOptionModule,
    MatSelectModule,
    SolutionChartComponent,
  ],
  templateUrl: './schedule-translator-debugger.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./schedule-translator-debugger.component.css'],
})
export class ScheduleTranslatorDebuggerComponent {
  private readonly api = inject(Api);
  protected readonly locationState = inject(LocationStateService);

  readonly path = getScheduleTranslatorDebugger.PATH;
  readonly timestepColumns = TIMESTEP_COLUMNS;
  readonly actionColumns = ACTION_COLUMNS;

  readonly selectedLocationId = computed(() => this.locationState.currentLocation()?.id ?? '');

  /** yyyy-MM-dd */
  readonly date = signal<string>(DAY_FORMAT.format(new Date()));

  readonly loading = signal(false);
  readonly errorMessage = signal<string | null>(null);
  readonly response = signal<ScheduleTranslatorDebuggerResponse | null>(null);

  /** Schlüssel der zuletzt kopierten Tabelle, für kurzes „Kopiert“-Feedback. */
  readonly copiedKey = signal<string | null>(null);
  private copiedResetTimer: ReturnType<typeof setTimeout> | undefined;

  readonly solutions = computed(() => this.response()?.solutions ?? []);

  /** Zuletzt manuell erstellter vs. zuletzt automatisch erzeugter Fahrplan über alle Lösungen des Tages. */
  readonly scheduleDiff = computed<ScheduleDiff | null>(() => {
    const latest = (generated: boolean): ScheduleWithSolution | undefined =>
      this.solutions()
        .flatMap((solution) => (solution.schedules ?? []).map((schedule) => ({ schedule, solution })))
        .filter(({ schedule }) => !!schedule.generated === generated)
        .reduce<ScheduleWithSolution | undefined>(
          (best, entry) => (!best || (entry.schedule.created ?? '') > (best.schedule.created ?? '') ? entry : best),
          undefined,
        );
    const manual = latest(false);
    const auto = latest(true);
    if (!manual && !auto) {
      return null;
    }
    return { manual, auto, rows: this.diffRows(manual, auto) };
  });

  readonly diffFields = DIFF_FIELDS;

  readonly configSourceLabels = CONFIG_SOURCE_LABELS;

  /** Konfiguration, nach Gruppe in der Reihenfolge des Backends. */
  readonly configGroups = computed(() => {
    const groups = new Map<string, ScheduleTranslatorDebuggerConfigValue[]>();
    for (const value of this.response()?.config ?? []) {
      const group = value.group ?? '–';
      groups.set(group, [...(groups.get(group) ?? []), value]);
    }
    return Array.from(groups, ([name, values]) => ({ name, label: CONFIG_GROUP_LABELS[name] ?? name, values }));
  });

  /** Lösung für das Diagramm; {@code null} folgt der neuesten Version des Tages. */
  readonly chartSolutionId = signal<number | null>(null);

  readonly chartSolution = computed(() => {
    const solutions = this.solutions();
    return solutions.find((solution) => solution.id === this.chartSolutionId()) ?? solutions[solutions.length - 1] ?? null;
  });

  /** Nur das Ergebnis der letzten Anfrage zählt, falls Standort oder Tag schnell wechseln. */
  private requestCounter = 0;

  constructor() {
    effect(() => {
      const locationId = this.selectedLocationId();
      const date = this.date();
      if (locationId && date) {
        void this.load(locationId, date);
      }
    });
  }

  setLocationId(locationId: string): void {
    const next = this.locationState.locations().find((location) => location.id === locationId);
    if (next) {
      this.locationState.setLocation(next);
    }
  }

  shiftDay(days: number): void {
    const [year, month, day] = this.date().split('-').map(Number);
    const shifted = new Date(Date.UTC(year, month - 1, day + days));
    this.date.set(shifted.toISOString().slice(0, 10));
  }

  today(): void {
    this.date.set(DAY_FORMAT.format(new Date()));
  }

  reload(): void {
    const locationId = this.selectedLocationId();
    if (locationId && this.date()) {
      void this.load(locationId, this.date());
    }
  }

  time(iso: string | undefined): string {
    return iso ? TIME_FORMAT.format(new Date(iso)) : '–';
  }

  timestamp(iso: string | undefined): string {
    return iso ? TIMESTAMP_FORMAT.format(new Date(iso)) : '–';
  }

  dateTime(iso: string | undefined): string {
    return iso ? DATE_TIME_FORMAT.format(new Date(iso)) : '–';
  }

  ctPerKwh(eurPerWh: number | undefined): number | undefined {
    return eurPerWh == null ? undefined : eurPerWh * EUR_PER_WH_TO_CT_PER_KWH;
  }

  /**
   * Uhrzeit (HH:mm), zu der eine Aktion feuert, gelesen aus Minuten- und Stundenfeld der Cron;
   * {@code null}, wenn diese Felder keine festen Zahlen sind. Akzeptiert 5- und 6-feldrige Crons.
   */
  actionTime(action: ScheduleTranslatorDebuggerAction): string | null {
    const fields = (action.cron ?? '').trim().split(/\s+/);
    if (fields.length < 5) {
      return null;
    }
    const [minute, hour] = fields.length >= 6 ? [fields[1], fields[2]] : [fields[0], fields[1]];
    if (!/^\d+$/.test(minute) || !/^\d+$/.test(hour)) {
      return null;
    }
    return `${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`;
  }

  /**
   * Voller Zeitstempel (Europe/Vienna), zu dem eine Aktion feuert. Tag, Monat und ggf. Jahr kommen aus der Cron,
   * sofern dort feste Zahlen stehen, sonst aus dem Lösungstag. {@code null}, wenn die Uhrzeit nicht fest ist.
   */
  actionTimestamp(action: ScheduleTranslatorDebuggerAction, solution: ScheduleTranslatorDebuggerSolution): string | null {
    const instant = this.actionInstant(action, solution);
    return instant ? TIMESTAMP_FORMAT.format(instant) : null;
  }

  private actionInstant(action: ScheduleTranslatorDebuggerAction, solution: ScheduleTranslatorDebuggerSolution): Date | null {
    const fields = (action.cron ?? '').trim().split(/\s+/);
    if (fields.length < 5) {
      return null;
    }
    // 5 Felder: min h dom mon dow; 6: s min h dom mon dow; 7: s min h dom mon dow year
    const [second, minute, hour, dayOfMonth, month, , year] = fields.length >= 6 ? fields : ['0', ...fields];
    const isNumber = (value: string | undefined): value is string => !!value && /^\d+$/.test(value);
    if (!isNumber(second) || !isNumber(minute) || !isNumber(hour)) {
      return null;
    }
    const [solutionYear, solutionMonth, solutionDay] = (solution.solutionDate ?? this.date()).split('-').map(Number);
    return viennaWallTimeToDate(
      isNumber(year) ? Number(year) : solutionYear,
      isNumber(month) ? Number(month) : solutionMonth,
      isNumber(dayOfMonth) ? Number(dayOfMonth) : solutionDay,
      Number(hour),
      Number(minute),
      Number(second),
    );
  }

  /** Uhrzeiten, zu denen in irgendeinem Fahrplan der Lösung eine Aktion feuert. */
  actionTimes(solution: ScheduleTranslatorDebuggerSolution): Set<string> {
    const times = new Set<string>();
    for (const schedule of solution.schedules ?? []) {
      for (const action of schedule.actions ?? []) {
        const time = this.actionTime(action);
        if (time) {
          times.add(time);
        }
      }
    }
    return times;
  }

  actionCount(solution: ScheduleTranslatorDebuggerSolution): number {
    return (solution.schedules ?? []).reduce((sum, schedule) => sum + (schedule.actions?.length ?? 0), 0);
  }

  diffCount(diff: ScheduleDiff, status: DiffStatus): number {
    return diff.rows.filter((row) => row.status === status).length;
  }

  /**
   * Stellt die Aktionen beider Fahrpläne zeilenweise gegenüber: Zuordnung über den Feuerzeitpunkt (bei mehreren
   * Aktionen zur selben Zeit in Reihenfolge), verglichen werden nur {@link DIFF_FIELDS} — Register-Felder nicht.
   */
  private diffRows(manual: ScheduleWithSolution | undefined, auto: ScheduleWithSolution | undefined): DiffRow[] {
    const keyed = (entry: ScheduleWithSolution | undefined) =>
      (entry?.schedule.actions ?? []).map((action) => {
        const instant = entry ? this.actionInstant(action, entry.solution) : null;
        return {
          action,
          key: instant ? `t${instant.getTime()}` : `c${action.cron ?? ''}`,
          sortValue: instant?.getTime() ?? Number.POSITIVE_INFINITY,
          time: instant ? TIMESTAMP_FORMAT.format(instant) : action.cron || '–',
        };
      });
    const manualActions = keyed(manual);
    const autoActions = keyed(auto);

    const rows: (DiffRow & { sortValue: number })[] = [];
    const unmatchedAuto = [...autoActions];
    for (const left of manualActions) {
      const index = unmatchedAuto.findIndex((right) => right.key === left.key);
      const right = index >= 0 ? unmatchedAuto.splice(index, 1)[0] : undefined;
      const changed = right ? DIFF_FIELDS.filter((field) => left.action[field.field] !== right.action[field.field]).map((field) => field.field) : [];
      rows.push({
        time: left.time,
        sortValue: left.sortValue,
        manual: left.action,
        auto: right?.action,
        changed,
        status: !right ? 'manualOnly' : changed.length ? 'changed' : 'same',
      });
    }
    for (const right of unmatchedAuto) {
      rows.push({ time: right.time, sortValue: right.sortValue, auto: right.action, changed: [], status: 'autoOnly' });
    }
    // stabil: innerhalb gleicher Zeit bleibt die Reihenfolge erhalten
    return rows.sort((a, b) => a.sortValue - b.sortValue);
  }

  /**
   * Kopiert die angezeigte Tabelle als TSV (Tab-getrennt, fügt sich sauber in Excel ein).
   * Kopfzeile: „Bezeichnung (Feldname)“; Zellen genau wie angezeigt.
   */
  async copyTable(table: HTMLTableElement, key: string): Promise<void> {
    const clean = (text: string | null | undefined) => (text ?? '').replace(/\s+/g, ' ').trim();
    const rows = Array.from(table.rows).map((row) =>
      Array.from(row.cells)
        .map((cell) => {
          const fieldName = cell.querySelector('.field-name');
          if (!fieldName) {
            return clean(cell.textContent);
          }
          const label = clean(Array.from(cell.childNodes).filter((node) => node !== fieldName).map((node) => node.textContent).join(''));
          return `${label} ${clean(fieldName.textContent)}`;
        })
        .join('\t'),
    );
    try {
      await navigator.clipboard.writeText(rows.join('\n'));
      this.copiedKey.set(key);
      clearTimeout(this.copiedResetTimer);
      this.copiedResetTimer = setTimeout(() => this.copiedKey.set(null), 1500);
    } catch {
      // Zugriff auf die Zwischenablage kann blockiert sein; die Tabelle steht ohnehin am Bildschirm.
    }
  }

  private async load(locationId: string, date: string): Promise<void> {
    const request = ++this.requestCounter;
    this.loading.set(true);
    this.errorMessage.set(null);
    try {
      const response = await this.api.invoke(getScheduleTranslatorDebugger, { location_id: locationId, date });
      if (request === this.requestCounter) {
        this.response.set(response);
      }
    } catch (error) {
      if (request === this.requestCounter) {
        this.response.set(null);
        this.errorMessage.set(this.formatError(error));
      }
    } finally {
      if (request === this.requestCounter) {
        this.loading.set(false);
      }
    }
  }

  private formatError(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      if (error.status === 0) {
        return 'Die API ist nicht erreichbar.';
      }
      const problem = (error.error && typeof error.error === 'object' ? error.error : undefined) as ApiProblem | undefined;
      const detail = [problem?.title, problem?.detail].filter(Boolean).join(' — ');
      return detail ? `${error.status}: ${detail}` : `${error.status} ${error.statusText}`;
    }
    return error instanceof Error ? error.message : String(error);
  }
}
